import { Check } from "@phosphor-icons/react"
import { Box } from "@tokenterminal/ui/Box"
import {
  ButtonV4,
  type ButtonV4Props,
} from "@tokenterminal/ui/Button/Button.v4"
import { LinkButtonV4 } from "@tokenterminal/ui/Button/LinkButton.v4"
import { Text } from "@tokenterminal/ui/Text"
import { Fragment, type ReactNode } from "react"
import {
  pricingTableTdCss,
  pricingTableThCss,
  tableCss,
  tableWrapperCss,
} from "./PricingTable.css"
import { BASE_PATH } from "@/constants/app.mjs"

type PricingTableProps = {
  selectedProduct?: "pro" | "api" | "data-room"
}

const CheckIcon = () => (
  <Check
    size={16}
    weight="bold"
    color="hsl(165, 89.40%, 63.10%)"
    style={{ display: "inline-block" }}
  />
)

const pricingTiers = [
  {
    label: "Free",
    id: "free",
  },
  {
    label: "Pro",
    id: "pro",
  },
  {
    label: "API",
    id: "api",
  },
  {
    label: "Data Room",
    id: "dataroom",
  },
] as const

const featureGroups: Array<{
  headingText: string
  features: Array<{
    feature: string
    free: boolean | string
    pro: boolean | string
    api: boolean | string
    dataroom: boolean | string
  }>
}> = [
  {
    headingText: "Data access",
    features: [
      {
        feature: "All historical data",
        free: true,
        pro: true,
        api: true,
        dataroom: true,
      },
      {
        feature: "All metrics for 100s of projects",
        free: true,
        pro: true,
        api: true,
        dataroom: true,
      },
      {
        feature: "Ability to download CSV, etc.",
        free: false,
        pro: true,
        api: true,
        dataroom: true,
      },
    ],
  },
  {
    headingText: "Studio",
    features: [
      {
        feature: "Number of dashboards",
        free: "3",
        pro: "Unlimited",
        api: "Unlimited",
        dataroom: "Unlimited",
      },
      {
        feature: "Number of charts",
        free: "3",
        pro: "Unlimited",
        api: "Unlimited",
        dataroom: "Unlimited",
      },
      {
        feature: "Ability to collaborate with team",
        free: false,
        pro: true,
        api: true,
        dataroom: true,
      },
    ],
  },
  {
    headingText: "Sheets - Beta",
    features: [
      {
        feature: "Access to Google Sheets Plugin",
        free: false,
        pro: true,
        api: true,
        dataroom: true,
      },
      {
        feature: "Access to Excel Plugin",
        free: false,
        pro: true,
        api: true,
        dataroom: true,
      },
      {
        feature: "Ability to pull metric and historical data",
        free: false,
        pro: true,
        api: true,
        dataroom: true,
      },
      {
        feature: "Monthly Rate Limits",
        free: "5,000 requests",
        pro: "500,000 requests",
        api: "1,000,000 requests",
        dataroom: "Custom",
      },
    ],
  },
  {
    headingText: "API",
    features: [
      {
        feature: "Access to REST API",
        free: false,
        pro: false,
        api: true,
        dataroom: true,
      },
      {
        feature: "SDKs included",
        free: false,
        pro: false,
        api: true,
        dataroom: true,
      },
      {
        feature: "Daily Rate Limits",
        free: false,
        pro: false,
        api: "250,000 per day",
        dataroom: "Custom",
      },
    ],
  },
  {
    headingText: "Data Warehouse",
    features: [
      {
        feature: "Access to Raw blockchain data",
        free: false,
        pro: false,
        api: false,
        dataroom: true,
      },
      {
        feature: "Access to all standardized, derived data",
        free: false,
        pro: false,
        api: false,
        dataroom: true,
      },
      {
        feature: "Big Query data room sharing",
        free: false,
        pro: false,
        api: false,
        dataroom: true,
      },
      {
        feature: "Snowflake data share",
        free: false,
        pro: false,
        api: false,
        dataroom: "Coming soon",
      },
    ],
  },
  {
    headingText: "Support",
    features: [
      {
        feature: "Chat support (During working hours)",
        free: true,
        pro: true,
        api: true,
        dataroom: true,
      },
      {
        feature: "Telegram support",
        free: false,
        pro: false,
        api: true,
        dataroom: true,
      },
      {
        feature: "SLA",
        free: false,
        pro: false,
        api: false,
        dataroom: true,
      },
    ],
  },
]

const productColumnWidth = "220px"

const CellContents = ({ value }: { value: boolean | ReactNode }) => {
  if (value === true) {
    return <CheckIcon />
  } else if (value === false) {
    return null
  } else if (typeof value === "string") {
    return (
      <Text color="muted" size="14">
        {value}
      </Text>
    )
  }

  return value
}

const PricingTable = ({ selectedProduct }: PricingTableProps) => {
  // const [annual, setAnnual] = useState(false)

  const CTAButton = ({
    emphasized,
    ...props
  }: ButtonV4Props & {
    emphasized?: boolean
  }) =>
    selectedProduct ? (
      <ButtonV4
        width="100%"
        variant={emphasized ? "muted" : "outline"}
        color={emphasized ? "success" : "neutral"}
        {...props}
      />
    ) : (
      <ButtonV4 width="100%" {...props} />
    )

  return (
    <Box className={tableWrapperCss}>
      <table className={tableCss} style={{}}>
        <thead>
          <tr>
            <th
              className={pricingTableThCss}
              style={{
                position: "sticky",
                top: 70,
                background: "#0d0d0d",
                zIndex: 2,
              }}
            />
            {pricingTiers.map((tier) => (
              <th
                className={pricingTableThCss}
                data-is-selected={selectedProduct === tier.id}
                style={{
                  width: productColumnWidth,
                  position: "sticky",
                  top: 70,
                  background: "#0d0d0d",
                  zIndex: 2,
                  fontWeight: "500",
                  fontSize: "16px",
                }}
              >
                {tier.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {featureGroups.map((featureGroup, index) => (
            <Fragment>
              <tr key={index}>
                <td className={pricingTableTdCss} style={{ borderTop: "none" }}>
                  <Text fontWeight="500" color="muted" marginTop="8x">
                    {featureGroup.headingText}
                  </Text>
                </td>
                {pricingTiers.map((tier) => (
                  <td
                    key={tier.id}
                    className={pricingTableTdCss}
                    style={{ borderTop: "none" }}
                  />
                ))}
              </tr>
              {featureGroup.features.map((feature, index) => (
                <tr key={index}>
                  <td
                    className={pricingTableTdCss}
                    style={{ borderTop: "1px solid #212121" }}
                  >
                    {feature.feature}
                  </td>
                  {pricingTiers.map((tier) => (
                    <td
                      data-is-selected={selectedProduct === tier.id}
                      className={pricingTableTdCss}
                      style={{}}
                    >
                      <CellContents value={feature[tier.id]} />
                    </td>
                  ))}
                </tr>
              ))}
            </Fragment>
          ))}
          <tr>
            <td
              className={pricingTableTdCss}
              style={{ paddingTop: "40px", borderTop: "1px solid #212121" }}
            ></td>
            <td className={pricingTableTdCss} style={{ paddingTop: "60px" }}>
              <LinkButtonV4
                width={"100%"}
                href={`${BASE_PATH}/explorer/auth/signup`}
                variant="outline"
              >
                Sign up for Free
              </LinkButtonV4>
            </td>
            <td
              className={pricingTableTdCss}
              data-is-selected={selectedProduct === "pro"}
              style={{ paddingTop: "60px" }}
            >
              <LinkButtonV4
                color="success"
                width={"100%"}
                href={`${BASE_PATH}/explorer/auth/signup`}
              >
                Start 7 day trial
              </LinkButtonV4>
            </td>
            <td
              className={pricingTableTdCss}
              data-is-selected={selectedProduct === "api"}
              style={{ paddingTop: "60px" }}
            >
              <LinkButtonV4
                width={"100%"}
                variant="outline"
                href={`${BASE_PATH}/explorer/auth/signup?returnTo=upgrade-to-api`}
              >
                Sign up for API
              </LinkButtonV4>
            </td>
            <td
              className={pricingTableTdCss}
              data-is-selected={selectedProduct === "data-room"}
              style={{ paddingTop: "60px" }}
            >
              <LinkButtonV4 width="100%" variant="outline" href="contact">
                Contact sales
              </LinkButtonV4>
            </td>
          </tr>
        </tbody>
      </table>
    </Box>
  )
}

export default PricingTable
